// /admin/v2/module-map — Module map editor (BRIEFS C4.4): which notes units each old module
// of a chapter belongs to. A helper only (narrows the AI link proposals, C7.1); Harsh never
// sees modules. ?chapter=<id> opens a chapter directly.
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v2AdminChapters, v2ModuleMap, v2SetModuleUnits, errText } from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel } from './V2Tabs';

const unitLabel = (u) => [u.number, u.title].filter(Boolean).join(' ') || u.code;

function ModuleRow({ m, units, chapterId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(new Set(m.units));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const byCode = Object.fromEntries(units.map((u) => [u.code, u]));

  const toggle = (code) => {
    const next = new Set(pick);
    if (next.has(code)) next.delete(code); else next.add(code);
    setPick(next);
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await v2SetModuleUnits(chapterId, m.code, units.filter((u) => pick.has(u.code)).map((u) => u.code));
      setOpen(false);
      onSaved(`Module ${m.code} saved.`);
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Could not save.') });
    } finally { setBusy(false); }
  };

  return (
    <tr data-testid={`v2-module-${m.code}`}>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}><strong>{m.code}</strong></td>
      <td style={{ verticalAlign: 'top' }}>{m.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
      <td style={{ verticalAlign: 'top', textAlign: 'right' }}>{m.questions}</td>
      <td style={{ verticalAlign: 'top' }}>
        {!open ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {m.units.length === 0 && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No unit yet</span>}
            {m.units.map((c) => (
              <span key={c} className="badge badge-blue" title={c}>{byCode[c] ? unitLabel(byCode[c]) : c}</span>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => { setPick(new Set(m.units)); setOpen(true); }}>Change</button>
          </div>
        ) : (
          <div>
            <Msg msg={msg} />
            <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
              {units.map((u) => (
                <label key={u.code} style={{
                  display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '3px 0',
                  paddingLeft: u.parent ? 16 : 0, fontStyle: u.kind === 'heading' ? 'italic' : 'normal',
                }}>
                  <input type="checkbox" checked={pick.has(u.code)} onChange={() => toggle(u.code)} />
                  {unitLabel(u)} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{u.code}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>Save</button>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminV2ModuleMap() {
  const [params, setParams] = useSearchParams();
  const chapterId = params.get('chapter') || '';
  const [msg, setMsg] = useState(null);
  const { data: chapters } = useQuery({ queryKey: ['v2-admin-chapters'], queryFn: v2AdminChapters });
  const withNotes = (chapters || []).filter((c) => c.points > 0);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['v2-module-map', chapterId],
    queryFn: () => v2ModuleMap(chapterId),
    enabled: !!chapterId,
  });

  return (
    <div>
      <V2Tabs title="Module map"
        intro="Link each old module of a chapter to the notes units it covers. This only helps the computer suggest the right notes point for each question; Harsh never sees modules." />
      <Msg msg={msg} />
      <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
        <select className="form-select" value={chapterId} style={{ maxWidth: 460 }}
          onChange={(e) => { setMsg(null); setParams(e.target.value ? { chapter: e.target.value } : {}); }}>
          <option value="">Choose a chapter…</option>
          {withNotes.map((c) => <option key={c.chapter_id} value={c.chapter_id}>{chapterLabel(c)}</option>)}
        </select>
      </div>
      {!chapterId ? null : isLoading ? <div className="spinner" /> : isError ? (
        <div className="card" style={{ color: 'var(--danger)' }}>Could not load the module map.</div>
      ) : data.modules.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-muted)' }}>This chapter has no modules.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr><th>Module</th><th>Name</th><th style={{ textAlign: 'right' }}>Questions</th><th>Notes units</th></tr>
            </thead>
            <tbody>
              {data.modules.map((m) => (
                <ModuleRow key={`${m.code}-${m.units.join(',')}`} m={m} units={data.units} chapterId={chapterId}
                  onSaved={async (t) => { await refetch(); setMsg({ kind: 'ok', text: t }); }} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
